import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import appLogo from '../../assets/png/appLogo.png';

export default function TwitchCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleTwitchCallback, currentUser } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState('');
  const [authCompleted, setAuthCompleted] = useState(false);
  const processedRef = useRef(false);
  const MAX_RETRIES = 12;

  useEffect(() => {
    const processCallback = async () => {
      // Prevent multiple executions
      if (processedRef.current) {
        return;
      }
      processedRef.current = true;

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const oauthError = searchParams.get('error');

      if (oauthError) {
        setError(`Twitch OAuth error: ${oauthError}`);
        setStatus('error');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setStatus('error');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          setStatus('processing');
          await handleTwitchCallback(code, state);
          setStatus('success');
          setAuthCompleted(true);
          return;
        } catch (err) {
          console.error(`Auth callback attempt ${attempt}/${MAX_RETRIES} failed:`, err);

          if (attempt < MAX_RETRIES) {
            // Wait before retrying (1s, 2s)
            await new Promise(resolve => setTimeout(resolve, attempt * 150));
          } else {
            setError(err.message);
            setStatus('error');
            setTimeout(() => navigate('/login'), 3000);
          }
        }
      }
    };

    processCallback();
  }, [searchParams, handleTwitchCallback, navigate]);

  // Wait for auth state to update before navigating
  useEffect(() => {
    if (authCompleted && currentUser) {
      // Auth has completed and user is now authenticated
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
    }
  }, [authCompleted, currentUser, navigate]);

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <div className="callback-content">
            <div className="callback-spinner">
              <FontAwesomeIcon icon={ICONS.SPINNER} spin />
            </div>
            <h2>{t('auth.twitch.processing')}</h2>
            <p>{t('auth.twitch.processingDescription')}</p>
          </div>
        );

      case 'success':
        return (
          <div className="callback-content success">
            <div className="callback-icon-wrapper">
              <FontAwesomeIcon icon={ICONS.CHECK_CIRCLE} />
            </div>
            <h2>{t('auth.twitch.success')}</h2>
            <p>{t('auth.twitch.successDescription')}</p>
            <div className="callback-redirect-bar">
              <div className="callback-redirect-fill"></div>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="callback-content error">
            <div className="callback-icon-wrapper">
              <FontAwesomeIcon icon={ICONS.EXCLAMATION_TRIANGLE} />
            </div>
            <h2>{t('auth.twitch.error')}</h2>
            <p className="callback-error-msg">{error}</p>
            <p>{t('auth.twitch.errorDescription')}</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container">
      <div className="auth-form">
        <div className="auth-logo-container">
          <img src={appLogo} alt="StreamQuest Logo" className="auth-logo" />
        </div>
        
        {renderContent()}
      </div>
    </div>
  );
} 